import { createContext, useContext, useState, useEffect } from 'react';
import { loginUser, logoutUser } from './user/UserActions';

const AuthContext = createContext();
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080/api/';
const LOCAL_STORAGE_KEY = 'halg-service-center.auth.user';

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Function to verify authentication status with backend
    const verifyAuth = async () => {
        try {
            const response = await fetch(`${BASE_URL}auth/verify`, {
                method: 'GET',
                credentials: 'include', // Include httpOnly cookies
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (process.env.REACT_APP_NODE_ENV !== 'production') console.log('Auth verify response:', response);
            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                setIsAuthenticated(true);
                if (process.env.REACT_APP_NODE_ENV !== 'production') console.log('User authenticated:', userData);
                // Keep local cache in sync
                // try { localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(userData)); } catch {}
                return true;
            } else {
                setUser(null);
                setIsAuthenticated(false);
                try { localStorage.removeItem(LOCAL_STORAGE_KEY); } catch {}
                return false;
            }
        } catch (error) {
            console.error('Auth verification failed:', error);
            setUser(null);
            setIsAuthenticated(false);
            try { localStorage.removeItem(LOCAL_STORAGE_KEY); } catch {}
            return false;
        }
    };

    useEffect(() => {
        // Hydrate from localStorage immediately to avoid UI flash
        try {
            const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (cached) {
                const cachedUser = JSON.parse(cached);
                setUser(cachedUser);
                setIsAuthenticated(true);
            }
        } catch {}

        // Verify with backend to ensure session is valid
        const checkAuth = async () => {
            if (process.env.REACT_APP_NODE_ENV !== 'production') console.log('Verifying authentication status on app load...');
            await verifyAuth();
            setLoading(false);
        };
        checkAuth();
    }, []);

    const login = async (credentials) => {
        try {
            const response = await loginUser(credentials);
            if (response.status === 200 && response.data) {
                setUser(response.data);
                setIsAuthenticated(true);
                // Persist user profile (no tokens) for UX
                try { localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(response.data)); } catch {}
                return { success: true };
            } else {
                return { success: false, error: response.error || 'Login failed' };
            }
        } catch (error) {
            return { success: false, error: 'An error occurred during login' };
        }
    };

    const logout = async () => {
        try {
            await logoutUser(); // uses httpOnly cookie under the hood
        } catch (error) {
            console.error('Logout request failed:', error);
        } finally {
            setUser(null);
            setIsAuthenticated(false);
            try { localStorage.removeItem(LOCAL_STORAGE_KEY); } catch {}
        }
    };

    const hasRole = (requiredRole) => {
        if (!user || !user.roles) return false;
        return user.roles.some(role => role.name === requiredRole || role === requiredRole);
    };

    const hasAnyRole = (requiredRoles) => {
        if (!user || !user.roles) return false;
        return requiredRoles.some(role => hasRole(role));
    };

    const hasAllRoles = (requiredRoles) => {
        if (!user || !user.roles) return false;
        return requiredRoles.every(role => hasRole(role));
    };

    const hasPrivilege = (requiredPrivilege) => {
        if (!user || !user.privileges) return false;
        return user.privileges.some(privilege => privilege.name === requiredPrivilege || privilege === requiredPrivilege);
    };

    const hasAnyPrivilege = (requiredPrivileges) => {
        if (!user || !user.privileges) return false;
        return requiredPrivileges.some(privilege => hasPrivilege(privilege));
    };

    const hasAllPrivileges = (requiredPrivileges) => {
        if (!user || !user.privileges) return false;
        return requiredPrivileges.every(privilege => hasPrivilege(privilege));
    };

    const hasRoleOrPrivilege = (roles = [], privileges = []) => {
    if (process.env.REACT_APP_NODE_ENV !== 'production') console.log('Checking roles or privileges:', roles, privileges);
        return hasAnyRole(roles) || hasAnyPrivilege(privileges);
    };

    const hasRoleAndPrivilege = (roles = [], privileges = []) => {
        return hasAnyRole(roles) && hasAnyPrivilege(privileges);
    };

    const isDispatchOnlyUser = (candidateUser = user) => {
        const roleNames = Array.isArray(candidateUser?.roles)
            ? candidateUser.roles
                .map(role => (typeof role === 'string' ? role : role?.name))
                .filter(Boolean)
                .map(name => String(name).toUpperCase())
            : [];

        if (roleNames.length === 0) return false;
        const unique = [...new Set(roleNames)];
        return unique.length === 1 && unique[0] === 'DISPATCH';
    };

    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        verifyAuth,
        hasRole,
        hasAnyRole,
        hasAllRoles,
        hasPrivilege,
        hasAnyPrivilege,
        hasAllPrivileges,
        hasRoleOrPrivilege,
        hasRoleAndPrivilege,
        isDispatchOnlyUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
